import { LEFT_NAV_WIDTH } from '@ben/orion/app-shell';
import { type ITheme } from '@ben/orion/utils/theme';
import { createGlobalStyle, css } from 'styled-components';

// Important: when adding/removing/updating custom properties, add them to the custom-properties.json file
// so that stylelint-value-no-unknown-custom-properties can catch them

export const buildCustomPropertiesFromTheme = (theme: ITheme) => {
  return css`
    // Palette custom properties
    --theme-palette-primary-main: ${theme.palette.primary.main};
    --theme-palette-primary-light: ${theme.palette.primary.light};
    --theme-palette-primary-dark: ${theme.palette.primary.dark};
    --theme-palette-primary-bg: ${theme.palette.primary.bg};
    --theme-palette-secondary-main: ${theme.palette.secondary.main};
    --theme-palette-secondary-light: ${theme.palette.secondary.light};
    --theme-palette-secondary-dark: ${theme.palette.secondary.dark};
    --theme-palette-secondary-bg: ${theme.palette.secondary.bg};
    --theme-palette-error-main: ${theme.palette.error.main};
    --theme-palette-error-light: ${theme.palette.error.light};
    --theme-palette-error-dark: ${theme.palette.error.dark};
    --theme-palette-error-bg: ${theme.palette.error.bg};
    --theme-palette-warning-main: ${theme.palette.warning.main};
    --theme-palette-warning-light: ${theme.palette.warning.light};
    --theme-palette-warning-dark: ${theme.palette.warning.dark};
    --theme-palette-warning-bg: ${theme.palette.warning.bg};
    --theme-palette-success-main: ${theme.palette.success.main};
    --theme-palette-success-light: ${theme.palette.success.light};
    --theme-palette-success-dark: ${theme.palette.success.dark};
    --theme-palette-success-bg: ${theme.palette.success.bg};
    --theme-palette-neutral-100: ${theme.palette.neutral['100']};
    --theme-palette-neutral-90: ${theme.palette.neutral['90']};
    --theme-palette-neutral-80: ${theme.palette.neutral['80']};
    --theme-palette-neutral-70: ${theme.palette.neutral['70']};
    --theme-palette-neutral-60: ${theme.palette.neutral['60']};
    --theme-palette-neutral-50: ${theme.palette.neutral['50']};
    --theme-palette-neutral-40: ${theme.palette.neutral['40']};
    --theme-palette-neutral-30: ${(
      theme.palette.neutral as Record<string, string>
    )['30']};
    --theme-palette-neutral-10: ${theme.palette.neutral['10']};
    --theme-palette-neutral-00: ${theme.palette.neutral['00']};
    --theme-palette-primary-light-33: ${theme.palette.primary.light}33;
    --theme-palette-focus: ${theme.palette.focus};
    --theme-palette-off-white: ${theme.palette.offWhite};

    // Spacing custom properties
    --theme-spacing-0: ${theme.spacing(0)};
    --theme-spacing-0-5: ${theme.spacing(0.5)};
    --theme-spacing-1: ${theme.spacing(1)};
    --theme-spacing-1-5: ${theme.spacing(1.5)};
    --theme-spacing-2: ${theme.spacing(2)};
    --theme-spacing-2-5: ${theme.spacing(2.5)};
    --theme-spacing-3: ${theme.spacing(3)};
    --theme-spacing-3-5: ${theme.spacing(3.5)};
    --theme-spacing-4: ${theme.spacing(4)};
    --theme-spacing-4-5: ${theme.spacing(4.5)};
    --theme-spacing-5: ${theme.spacing(5)};
    --theme-spacing-5-5: ${theme.spacing(5.5)};
    --theme-spacing-6: ${theme.spacing(6)};
    --theme-spacing-6-5: ${theme.spacing(6.5)};
    --theme-spacing-7: ${theme.spacing(7)};
    --theme-spacing-7-5: ${theme.spacing(7.5)};
    --theme-spacing-8: ${theme.spacing(8)};
    --theme-spacing-8-5: ${theme.spacing(8.5)};
    --theme-spacing-9: ${theme.spacing(9)};
    --theme-spacing-9-5: ${theme.spacing(9.5)};
    --theme-spacing-10: ${theme.spacing(10)};
    --theme-spacing-10-5: ${theme.spacing(10.5)};
    --theme-spacing-11: ${theme.spacing(11)};
    --theme-spacing-11-5: ${theme.spacing(11.5)};
    --theme-spacing-12: ${theme.spacing(12)};

    // Typography custom properties
    --theme-typography-h1-font-size: ${theme.typography.h1.fontSize};
    --theme-typography-h1-font-weight: ${theme.typography.h1.fontWeight};
    --theme-typography-h1-line-height: ${theme.typography.h1.lineHeight};
    --theme-typography-h1-letter-spacing: ${theme.typography.h1.letterSpacing};
    --theme-typography-h2-font-size: ${theme.typography.h2.fontSize};
    --theme-typography-h2-font-weight: ${theme.typography.h2.fontWeight};
    --theme-typography-h2-line-height: ${theme.typography.h2.lineHeight};
    --theme-typography-h2-letter-spacing: ${theme.typography.h2.letterSpacing};
    --theme-typography-h3-font-size: ${theme.typography.h3.fontSize};
    --theme-typography-h3-font-weight: ${theme.typography.h3.fontWeight};
    --theme-typography-h3-line-height: ${theme.typography.h3.lineHeight};
    --theme-typography-h3-letter-spacing: ${theme.typography.h3.letterSpacing};
    --theme-typography-body-lg1-font-size: ${theme.typography.bodyLg1.fontSize};
    --theme-typography-body-lg1-font-weight: ${theme.typography.bodyLg1
      .fontWeight};
    --theme-typography-body-lg1-line-height: ${theme.typography.bodyLg1
      .lineHeight};
    --theme-typography-body-lg1-letter-spacing: ${theme.typography.bodyLg1
      .letterSpacing};
    --theme-typography-body-lg2-font-size: ${theme.typography.bodyLg2.fontSize};
    --theme-typography-body-lg2-font-weight: ${theme.typography.bodyLg2
      .fontWeight};
    --theme-typography-body-lg2-line-height: ${theme.typography.bodyLg2
      .lineHeight};
    --theme-typography-body-lg2-letter-spacing: ${theme.typography.bodyLg2
      .letterSpacing};
    --theme-typography-body-lg3-font-size: ${theme.typography.bodyLg3.fontSize};
    --theme-typography-body-lg3-font-weight: ${theme.typography.bodyLg3
      .fontWeight};
    --theme-typography-body-lg3-line-height: ${theme.typography.bodyLg3
      .lineHeight};
    --theme-typography-body-lg3-letter-spacing: ${theme.typography.bodyLg3
      .letterSpacing};
    --theme-typography-body-md1-font-size: ${theme.typography.bodyMd1.fontSize};
    --theme-typography-body-md1-font-weight: ${theme.typography.bodyMd1
      .fontWeight};
    --theme-typography-body-md1-line-height: ${theme.typography.bodyMd1
      .lineHeight};
    --theme-typography-body-md1-letter-spacing: ${theme.typography.bodyMd1
      .letterSpacing};
    --theme-typography-body-md2-font-size: ${theme.typography.bodyMd2.fontSize};
    --theme-typography-body-md2-font-weight: ${theme.typography.bodyMd2
      .fontWeight};
    --theme-typography-body-md2-line-height: ${theme.typography.bodyMd2
      .lineHeight};
    --theme-typography-body-md2-letter-spacing: ${theme.typography.bodyMd2
      .letterSpacing};
    --theme-typography-body-md3-font-size: ${theme.typography.bodyMd3.fontSize};
    --theme-typography-body-md3-font-weight: ${theme.typography.bodyMd3
      .fontWeight};
    --theme-typography-body-md3-line-height: ${theme.typography.bodyMd3
      .lineHeight};
    --theme-typography-body-md3-letter-spacing: ${theme.typography.bodyMd3
      .letterSpacing};
    --theme-typography-label-lg-font-size: ${theme.typography.labelLg.fontSize};
    --theme-typography-label-lg-font-weight: ${theme.typography.labelLg
      .fontWeight};
    --theme-typography-label-lg-line-height: ${theme.typography.labelLg
      .lineHeight};
    --theme-typography-label-lg-letter-spacing: ${theme.typography.labelLg
      .letterSpacing};
    --theme-typography-label-lg-text-transform: ${theme.typography.labelLg
      .textTransform};
    --theme-typography-label-sm-font-size: ${theme.typography.labelSm.fontSize};
    --theme-typography-label-sm-font-weight: ${theme.typography.labelSm
      .fontWeight};
    --theme-typography-label-sm-line-height: ${theme.typography.labelSm
      .lineHeight};
    --theme-typography-label-sm-letter-spacing: ${theme.typography.labelSm
      .letterSpacing};
    --theme-typography-label-sm-text-transform: ${theme.typography.labelSm
      .textTransform};
    --theme-typography-body-sm1-font-size: ${theme.typography.bodySm1.fontSize};
    --theme-typography-body-sm1-font-weight: ${theme.typography.bodySm1
      .fontWeight};
    --theme-typography-body-sm1-line-height: ${theme.typography.bodySm1
      .lineHeight};
    --theme-typography-body-sm1-letter-spacing: ${theme.typography.bodySm1
      .letterSpacing};
    --theme-typography-body-sm2-font-size: ${theme.typography.bodySm2.fontSize};
    --theme-typography-body-sm2-font-weight: ${theme.typography.bodySm2
      .fontWeight};
    --theme-typography-body-sm2-line-height: ${theme.typography.bodySm2
      .lineHeight};
    --theme-typography-body-sm2-letter-spacing: ${theme.typography.bodySm2
      .letterSpacing};
    --theme-typography-font-family: ${theme.fontFamily};

    // Non-theme custom properties
    --left-nav-width: ${LEFT_NAV_WIDTH};

    // Border radius
    --theme-border-radius-sm: ${theme.borderRadius.sm};
    --theme-border-radius-md: ${theme.borderRadius.md};
    --theme-border-radius-lg: ${theme.borderRadius.lg};
    --theme-border-radius-full: ${theme.borderRadius.full};

    // Components
    --theme-component-input-height: ${theme.input.select.height};
  `;
};

const GlobalCssCustomProperties = createGlobalStyle`
    :root {
      ${({ theme }) => buildCustomPropertiesFromTheme(theme)}
    }
  `;

export default GlobalCssCustomProperties;
